import { useMemo } from 'react';
import Decimal from 'decimal.js';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { v4 as uuidv4 } from 'uuid';
import { ModelDTO } from '../../api/useModelGroupsQuery';
import { useDefaultAssetAllocation } from '../../hooks/useDefaultAssetAllocation';
import { useAssetsColors } from '../../hooks/useAssetsColors';
import { formatNumber } from '../../../../../CommonUtils/formatNumber';
import { FormValues, validation } from './UpsertModel.validation';
import { TEMP_PREFIX } from '../../Models.constants';
import { computeRiskBands } from '../../utils/computeRiskBands';
import { UpsertModelGroupActions } from '../UpsertModelGroup/useUpsertModelGroup';

interface UseUpsertModelParams {
  isReadOnly?: boolean;
  model: Partial<ModelDTO>;
  models: ModelDTO[];
  modelsActions?: UpsertModelGroupActions;
  onClose(): void;
}

export const useUpsertModel = ({
  model,
  models,
  modelsActions,
  isReadOnly,
  onClose,
}: UseUpsertModelParams) => {
  const { assetAllocationInputModel } = model;

  const defaultAssetAllocation = useDefaultAssetAllocation();

  const assetAllocation = useMemo(() => {
    if (!assetAllocationInputModel || !defaultAssetAllocation) {
      return null;
    }

    return defaultAssetAllocation.map(asset => {
      const foundAsset = assetAllocationInputModel.find(({ id }) => id === asset.id);

      return {
        ...asset,
        y: foundAsset ? new Decimal(foundAsset.y).times(100).toNumber() : 0,
      };
    });
  }, [assetAllocationInputModel, defaultAssetAllocation]);

  const assetColors = useAssetsColors();

  const defaultValues = {
    orionModelId: model.orionModelId ? model.orionModelId.toString() : '',
    modelName: model.portfolioName || '',
    modelDescription: model.portfolioDescription || '',
    hypotheticalReturn:
      typeof model.hypotheticalReturn === 'number'
        ? formatNumber(new Decimal(model.hypotheticalReturn).times(100).toNumber(), {
            decimalPlaces: 2,
            thousandSeparated: false,
          })
        : '',
    assetAllocation: assetAllocation || defaultAssetAllocation,
  };

  const formBag = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(validation),
    context: {
      model,
      models,
    },
  });

  const { fields: allocationFields } = useFieldArray({
    name: 'assetAllocation',
    control: formBag.control,
    keyName: 'key',
  });

  const watchedAllocation = formBag.watch('assetAllocation');
  const controlledAllocationFields = allocationFields.map((field, index) => {
    return {
      ...field,
      ...(watchedAllocation ? watchedAllocation[index] : {}),
    };
  });

  const [allocationTotal, isAllocationInvalid] = useMemo(() => {
    const total = Object.values(controlledAllocationFields).reduce((acc, currentAssetValue) => {
      if (currentAssetValue.y) {
        return new Decimal(acc).plus(currentAssetValue.y).toNumber();
      }

      return acc;
    }, 0);

    const isInvalid = total !== 100;

    return [total, isInvalid];
  }, [controlledAllocationFields]);

  const onSubmit = (values: FormValues) => {
    // Do not submit if allocation is invalid or model is readonly
    if (isAllocationInvalid || isReadOnly) {
      return;
    }

    if (!values.assetAllocation || !modelsActions) {
      return;
    }

    const parsedAssetAllocation = values.assetAllocation.map(asset => {
      return {
        ...asset,
        y: asset.y ? new Decimal(asset.y).div(100).toNumber() : 0,
      };
    });

    const parsedModel: ModelDTO = {
      ...model,
      id: model.id || `${TEMP_PREFIX}${uuidv4()}`,
      orionModelId: values.orionModelId ? Number(values.orionModelId) : null,
      riskScore: model.riskScore || null,
      portfolioName: values.modelName,
      portfolioDescription: values.modelDescription,
      hypotheticalReturn:
        values.hypotheticalReturn !== ''
          ? new Decimal(values.hypotheticalReturn).div(100).toNumber()
          : null,
      assetAllocationInputModel: parsedAssetAllocation,
      riskRange: model.riskRange || { from: 1, to: 100 },
    };

    // We are updating a model
    if (model.id) {
      const newModels = models.map(_model => {
        return model.id === _model.id ? parsedModel : _model;
      });

      modelsActions.setModels(newModels);
    } else {
      // We are adding a new model ➜ we need to recalculate the risk bands
      const riskBands = computeRiskBands(models.length + 1);

      const newModels = models.map((_model, index) => ({
        ..._model,
        riskRange: {
          from: riskBands[index][0],
          to: riskBands[index][1],
        },
      }));

      modelsActions.setModels([
        ...newModels,
        {
          ...parsedModel,
          riskRange: {
            from: riskBands[models.length][0],
            to: riskBands[models.length][1],
          },
        },
      ]);
    }

    onClose();
  };

  return {
    formBag,
    allocationFields: controlledAllocationFields,
    assetColors,
    isAllocationInvalid,
    allocationTotal,
    onSubmit,
  };
};
